import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, SectionTitle } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { playChime, alertSoundEnabled, setAlertSound } from "@/lib/chime";
import { Gauge, User, Info, Volume2 } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [threshold, setThreshold] = useState(75);
  const [busy, setBusy] = useState(false);
  const [soundOn, setSoundOn] = useState(alertSoundEnabled());

  useEffect(() => {
    api.get("/settings").then(({ data }) => setThreshold(data.confidence_threshold)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", { confidence_threshold: threshold });
      toast.success(`AI confidence threshold set to ${threshold}%`);
    } catch {
      toast.error("Failed to save settings");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 ngx-fade-in max-w-3xl">
      <SectionTitle sub="Configure NGX Core behaviour and review operator session.">Settings</SectionTitle>

      <Card className="p-6" data-testid="threshold-card">
        <div className="flex items-center gap-2 mb-1 text-slate-200">
          <Gauge className="h-4 w-4 text-blue-400" />
          <h3 className="font-display font-semibold">AI Confidence Threshold</h3>
        </div>
        <p className="text-sm text-slate-400 mb-6">
          When simulated AI confidence drops below this value, NGX pauses the task and requests a human decision.
        </p>
        <div className="flex items-center gap-6">
          <div className="font-mono text-5xl font-medium text-blue-400 w-28">{threshold}%</div>
          <div className="flex-1">
            <Slider
              data-testid="threshold-slider"
              value={[threshold]} min={0} max={100} step={1}
              onValueChange={(v) => setThreshold(v[0])}
            />
            <div className="flex justify-between text-xs text-slate-500 mt-2 font-mono">
              <span>0%</span><span>50%</span><span>100%</span>
            </div>
          </div>
        </div>
        <Button data-testid="save-threshold" onClick={save} disabled={busy}
          className="mt-6 h-11 bg-blue-600 hover:bg-blue-700 text-white">
          Save Threshold
        </Button>
      </Card>

      <Card className="p-6" data-testid="alert-sound-card">
        <div className="flex items-center gap-2 mb-1 text-slate-200">
          <Volume2 className="h-4 w-4 text-blue-400" />
          <h3 className="font-display font-semibold">Decision Alert Sound</h3>
        </div>
        <p className="text-sm text-slate-400 mb-5">
          Play a short, soft notification chime when NGX requests a human decision. This helps attract operator
          attention without alarm fatigue.
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Switch
              data-testid="alert-sound-toggle" checked={soundOn}
              onCheckedChange={(v) => { setSoundOn(v); setAlertSound(v); toast.success(`Decision alert sound ${v ? "enabled" : "disabled"}`); }}
            />
            <span className="font-mono text-sm text-slate-300">{soundOn ? "ON" : "OFF"}</span>
          </div>
          <Button data-testid="test-alert" onClick={() => playChime("decision")}
            className="h-10 bg-[#050B14] border border-slate-700 text-slate-200 hover:border-blue-500">
            Test Alert
          </Button>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4 text-slate-200">
          <User className="h-4 w-4 text-blue-400" />
          <h3 className="font-display font-semibold">Operator Session</h3>
        </div>
        <div className="grid grid-cols-2 gap-4 font-mono text-sm">
          <Field label="Operator" value={user?.name} />
          <Field label="Email" value={user?.email} />
          <Field label="Role" value={user?.role} />
          <Field label="Access Level" value={user?.level} />
        </div>
      </Card>

      <Card className="p-5 border-amber-900/40 bg-amber-950/10 flex items-start gap-3">
        <Info className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
        <div className="space-y-3">
          <p className="text-sm text-slate-300">
            This prototype runs entirely in <span className="text-amber-300 font-medium">Simulation Mode</span>. No physical
            equipment is connected. Future integrations (robots, PLCs, real vision systems, OPC UA, MQTT, edge devices,
            cloud deployment, model retraining) are architectural targets and are not functional in this MVP.
          </p>
          <p className="text-sm text-red-300/90 font-medium" data-testid="settings-safety-disclaimer">
            NGX simulation safety controls do not replace certified industrial safety systems.
          </p>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="text-slate-100">{value || "—"}</div>
    </div>
  );
}
